import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Building2, MapPin, Calendar, TrendingUp, FileText, Receipt,
  ClipboardList, LogOut, ChevronRight, CreditCard
} from "lucide-react";
import GlassLayout from "@/components/GlassLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getStoredUser() {
  const fromLocal = localStorage.getItem('user');
  const fromSession = sessionStorage.getItem('user');
  const raw = fromLocal || fromSession;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (error) { return null; }
}

function ProgressBar({ value }) {
  return (
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);

  const fetchProjects = useCallback(async (clientId) => {
    try {
      const res = await axios.get(`${API}/client-portal/my-projects?client_id=${clientId}`);
      setProjects(res.data || []);
    } catch (err) {
      toast.error("Eroare la încărcarea proiectelor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const u = getStoredUser();
    if (!u || u.role !== "client") {
      navigate("/login");
      return;
    }
    setUser(u);
    fetchProjects(u.id);
  }, [navigate, fetchProjects]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    localStorage.removeItem("rememberMe");
    navigate("/login");
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <GlassLayout data-testid="client-dashboard">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 text-base" data-testid="client-name">{user.name}</h1>
              <p className="text-xs text-slate-500">Portal Client</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-1.5" /> Deconectare
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {selectedSite ? (
          <ClientProjectDetail
            site={selectedSite}
            clientId={user.id}
            onBack={() => setSelectedSite(null)}
          />
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-800 mb-4">Proiectele mele</h2>

            {projects.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nu aveți proiecte asociate încă.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {projects.map((site) => (
                  <Card key={site.id} className="hover:shadow-md transition-shadow cursor-pointer group" data-testid={`project-card-${site.id}`}
                    onClick={() => setSelectedSite(site)}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-800 truncate">{site.name}</h3>
                          {site.address && (
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3.5 h-3.5" />{site.address}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors flex-shrink-0 mt-1" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Progres</span>
                          <span className="font-bold text-teal-600">{(site.progress_percent || 0).toFixed(0)}%</span>
                        </div>
                        <ProgressBar value={site.progress_percent || 0} />
                      </div>
                      {site.start_date && (
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />{site.start_date} — {site.end_date || "în curs"}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </GlassLayout>
  );
}
